import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import {
  fetchStructuralRev,
  postStructuralRev,
  fetchAllRevsForProject,
  updateStructuralRev,
  getProjectById,
} from "./api";

import { useParams } from "react-router-dom";
import { useGeneralContext } from "../../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";

import {
  getComponentTab,
  getSizesOptions,
  updateDerivedFields,
} from "./StructureModelingTabs/PartLibTab/utils";

import { secondAxios } from "../../../../../api/axios";

const STRUCTURAL_TYPE = "structural";

const ALL_PART_LIB_TABS = [
  "Angles",
  "Beams",
  "Channels",
  "Tubes",
  "Flatbars",
  "Gratings",
  "Plates",
  "Bolts",
  "Nuts",
  "Washers",
];

const getRowsFromSqliteResponse = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  return [];
};

/**
 * @typedef {Object} StructureModelingContextValue
 * @property {*} partLibTable
 * @property {Function} setPartLibTable
 * @property {*} specTable
 * @property {Function} setSpecTable
 * @property {*} estimationTable
 * @property {Function} setEstimationTable
 * @property {Object} allTables
 * @property {Function} initializeAllTables
 * @property {{label: string, spec: string, rev: string | number}[]} selectedSpecs
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} addSlectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} removeSelectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => Object} getSelectedSpecTable
 * @property {Function} getComponentCodeTable
 * @property {Object} modelingData
 * @property {Function} setModelingData
 * @property {Function} saveStructuralData
 * @property {Function} setProjectPath
 * @property {string | null} projectPath
 */

/** @type {React.Context<StructureModelingContextValue | undefined>} */
const StructureModelingContext = createContext(undefined);

/**
 * StructureModelingProvider - Context provider for structure modeling state.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const StructureModelingProvider = ({ children }) => {
  const [allTables, setAllTables] = useState({});
  const [specTable, setSpecTable] = useState([]);
  const [fullSpecTable, setFullSpecTable] = useState([]);
   const[selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [partLibTable, setPartLibTable] = useState({});
  const [estimationTable, setEstimationTable] = useState({ 0: {} });
  const [modelingData, setModelingData] = useState({});
  const [revision, setRevision] = useState(undefined);
  const [projectPath, setProjectPath] = useState(null);
  const [selectedModelingRevision, setSelectedModelingRevision] = useState("0");
  const [selectedEstimationRevision, setSelectedEstimationRevision] =
    useState("0");
  const [clientName, setClientName] = useState("");

  const { fastenersTable, setFastenersTable } = useGeneralContext();

  const [bomData, setBomData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [estimationTableWithTotalSummary, setEstimationTableWithTotalSummary] =
    useState({});

  const [totalSummary, setTotalSummary] = useState([
    { name: "Total Materials Cost", value: "", unit: "$", rate: "", total: "" },
    { name: "Total Man Hours", value: "", unit: "hr", rate: "", total: "" },
    { name: "Galvanized Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Painting Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Fire Proof Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Miscellaneous", value: "", unit: "LS", rate: "1", total: "" },
  ]);

  const [allCombinationTable, setAllCombinationTable] = useState([]);

  const SelectedParts = useMemo(() => {
    const byTab = {};

    (allCombinationTable || []).forEach((row) => {
      if (row.select && row.TabName) {
        if (!byTab[row.TabName]) byTab[row.TabName] = [];
        byTab[row.TabName].push(row);
      }
    });

    return byTab;
  }, [allCombinationTable]);


  function processSpecData(rows) {
    const seen = new Map();

    for (const row of rows) {
      const key = `${row.Spec}|${row.Components}|${row.Size}|${row.Material}|${row.Requirements}|${row.Rev}`;

      if (!seen.has(key)) {
        seen.set(key, row);
      }
    }

    const uniqueRows = Array.from(seen.values());

    return uniqueRows.sort((a, b) => {
      const compCompare = (a.Components || "").localeCompare(
        b.Components || "",
      );

      if (compCompare !== 0) return compCompare;

      const sizeA = parseFloat(a.Size) || 0;
      const sizeB = parseFloat(b.Size) || 0;

      if (sizeA !== sizeB) return sizeA - sizeB;

      return (a.Size || "").localeCompare(b.Size || "");
    });
  }

  const initializeAllTables = useCallback(async () => {
    try {
      const tableNamesResponse = await secondAxios.post(`/sqlite-db/query`, {
        dbName: "structure",
        sql: `
          SELECT name
          FROM sqlite_master
          WHERE type = 'table'
          ORDER BY name
        `,
      });

      const tableNameRows = getRowsFromSqliteResponse(tableNamesResponse);

      const tableNames = tableNameRows
        .map((row) => row?.name)
        .filter(Boolean);

      const tables = {};

      for (const tableName of tableNames) {
        const safeTableName = String(tableName).replace(/"/g, '""');

        const tableDataResponse = await secondAxios.post(`/sqlite-db/query`, {
          dbName: "structure",
          sql: `SELECT * FROM "${safeTableName}"`,
        });

        tables[tableName] = getRowsFromSqliteResponse(tableDataResponse);
      }

      setAllTables(tables);

      const allSpecData = [];

      const specTableKeys = Object.keys(tables || {}).filter((key) =>
        key.startsWith("structural_spec_"),
      );

      for (const specKey of specTableKeys) {
        const specData = Array.isArray(tables[specKey])
          ? tables[specKey]
          : [];

        allSpecData.push(...specData);
      }

      setFullSpecTable([...allSpecData]);

      const uniqueSpecData = processSpecData(allSpecData);
      setSpecTable(uniqueSpecData);
    } catch (error) {
      console.error("Failed to initialize structural tables:", error);

      setAllTables({});
      setFullSpecTable([]);
      setSpecTable([]);
    }
  }, []);

  function addSlectedSpec(newSelc) {
    setSelectedSpecs((prev) => {
      const exists = prev.some((item) => item.label === newSelc.label);
      return exists ? prev : [...prev, newSelc];
    });
  }

  function removeSelectedSpec(toRemove) {
    setSelectedSpecs((prev) =>
      prev.filter((item) => item.label !== toRemove.label),
    );
  }

  const getSelectedSpecTable = useCallback(
    (selc) => {
      if (!selc) return [];
      if (!specTable || !Array.isArray(specTable)) return [];

      return specTable.filter(
        (item) =>
          item.Spec === selc.spec &&
          item.Rev?.toString() === selc.rev?.toString(),
      );
    },
    [specTable],
  );

  function getComponentCodeTable() {
    return allTables["mc_components"];
  }

  const addNewEstimationRevision = useCallback(() => {
    const revisions = Object.keys(estimationTable)
      .filter((key) => !isNaN(Number(key)))
      .map(Number)
      .sort((a, b) => a - b);

    const nextRev = String(
      revisions.length > 0 ? Math.max(...revisions) + 1 : 1,
    );

    const latestRev =
      revisions.length > 0 ? String(revisions[revisions.length - 1]) : "0";

    const latestData = estimationTable[latestRev] || {};

    setEstimationTable((prev) => ({
      ...prev,
      [nextRev]: JSON.parse(JSON.stringify(latestData)),
    }));

    setSelectedEstimationRevision(nextRev);

    return nextRev;
  }, [estimationTable]);

  function buildStructuralPayload() {
    console.log("estimationTable total summary", totalSummary);

    const rev = selectedEstimationRevision;
    const currentRevData = estimationTable[rev] || {};

    const updatedEstimationTable = {
      ...estimationTable,
      [rev]: {
        ...currentRevData,
        totalSummary: totalSummary || [],
      },
    };

    setEstimationTableWithTotalSummary(updatedEstimationTable);

    if (currentRevData && currentRevData.BOM) {
      const bomData1 = currentRevData.BOM.map((item) => ({
        PnPID: null,
        "Cost Code": "Structure",
        Description: item["Material Description"],
        Qty: item.Qty,
        Unit: item.Unit,
        "Weight (lb)": item.Weight,
        "Cost (US $)": item.MaterialCost,
      }));

      setBomData(bomData1);
    }

    if (currentRevData && currentRevData.Summary) {
      const summaryData1 = currentRevData.Summary.filter(
        (cur) => !cur.isSummary,
      ).map((item) => ({
        PnPID: null,
        "Cost Code": "Structure",
        Components: item.Components,
        "Material Description": item["Material Description"],
        Qty: item.Qty,
        "Total Weight (lb)": item.Weight,
        "Total Material Cost (US $)": item.MaterialCost,
        "Total Manhour": +(
          (item.WeldHours || 0) +
          (item.FireProofingCost || 0) +
          (item.GalvanizingCost || 0) +
          (item.PaintingCost || 0)
        ).toFixed(2),
        Remarks: null,
      }));

      setSummaryData(summaryData1);
    }

    const payload = {
      name: selectedProjectId,
      type: STRUCTURAL_TYPE,
      revision: revision?.id,
      data: {
        selectedSpecs,
        partLibTable,
        SelectedParts,
        estimationTable: updatedEstimationTable,
        modelingData,
        projectPath,
        Est_BOM_Summary_DGV: bomData || [],
        DGV_Estimation_Est: summaryData || [],
      },
    };

    return payload;
  }

  async function saveStructuralData() {
    const data = buildStructuralPayload();

    console.log("saving", data);

    if (revision?.id) {
      await updateStructuralRev(selectedProjectId, revision.id, data);
    } else {
      const rev = await postStructuralRev(selectedProjectId, {
        name: "0",
        data: data?.data || {},
      });

      console.log("Created new revision:", rev);
      setRevision(rev);
    }
  }

  useEffect(() => {
    window.__menuHandlers = window.__menuHandlers || {};

    window.__menuHandlers.SAVE = saveStructuralData;
    window.__menuHandlers.ADD_ESTIMATION_REV = addNewEstimationRevision;

    return () => {
      if (window.__menuHandlers?.SAVE === saveStructuralData) {
        window.__menuHandlers.SAVE = null;
      }

      if (
        window.__menuHandlers?.ADD_ESTIMATION_REV === addNewEstimationRevision
      ) {
        window.__menuHandlers.ADD_ESTIMATION_REV = null;
      }
    };
  }, [
    revision,
    selectedModelingRevision,
    selectedEstimationRevision,
    selectedSpecs,
    partLibTable,
    SelectedParts,
    modelingData,
    projectPath,
    estimationTableWithTotalSummary,
    summaryData,
    bomData,
    estimationTable,
    totalSummary,
    addNewEstimationRevision,
  ]);

  function parseStructuralData(raw) {
    const data = raw?.data || {};

    console.log("Parsed structural data:", data);

    setProjectPath(data.projectPath || null);
    setSelectedSpecs(data.selectedSpecs || []);

    setPartLibTable(() => {
      const saved = data.partLibTable;

      if (!saved || Object.keys(saved).length === 0) return {};

      const keys = Object.keys(saved);

      const looksLikeLegacyRevisioned = keys.every(
        (k) =>
          /^\d+$/.test(k) &&
          saved[k] &&
          typeof saved[k] === "object" &&
          !Array.isArray(saved[k]),
      );

      if (looksLikeLegacyRevisioned) {
        const lastRev = keys[keys.length - 1];
        return saved[lastRev] || {};
      }

      return saved;
    });

    let parsedEstimationTable = {};

    if (data.estimationTable && Object.keys(data.estimationTable).length > 0) {
      parsedEstimationTable = data.estimationTable;
    } else {
      parsedEstimationTable = { 0: {} };
    }

    setEstimationTable(parsedEstimationTable);
    setEstimationTableWithTotalSummary(parsedEstimationTable);

    const revisions = Object.keys(parsedEstimationTable)
      .filter((key) => !isNaN(Number(key)))
      .sort((a, b) => Number(a) - Number(b));

    const latestRevision =
      revisions.length > 0 ? revisions[revisions.length - 1] : "0";

    setSelectedEstimationRevision(latestRevision);

    const currentRevisionData = parsedEstimationTable[latestRevision] || {};

    if (currentRevisionData.totalSummary) {
      setTotalSummary(currentRevisionData.totalSummary);
    }

    if (data.Est_BOM_Summary_DGV && data.DGV_Estimation_Est) {
      setBomData(data.Est_BOM_Summary_DGV);
      setSummaryData(data.DGV_Estimation_Est);
    } else if (currentRevisionData.BOM || currentRevisionData.Summary) {
      if (currentRevisionData.BOM) {
        setBomData(currentRevisionData.BOM);
      }

      if (currentRevisionData.Summary) {
        setSummaryData(currentRevisionData.Summary);
      }
    }

    setModelingData(() => {
      if (!data.modelingData || Object.keys(data.modelingData).length === 0) {
        return {
          0: {
            templatePath: null,
            categories: [],
          },
        };
      }

      return data.modelingData;
    });
  }

  async function loadStructuralData(projectIdValue) {
    const revisions = await fetchAllRevsForProject(projectIdValue);
    let rev;

    if (!revisions.length) {
      rev = await postStructuralRev(projectIdValue, {
        name: "0",
        data: "{}",
      });
    } else {
      rev = revisions[revisions.length - 1];
    }

    console.log("Loaded revision:", rev);

    setRevision(rev);

    const jsonData = await fetchStructuralRev(projectIdValue, rev.id);
    parseStructuralData(jsonData);
  }

  useEffect(() => {
    if (selectedProjectId) {
      loadStructuralData(selectedProjectId);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;

    async function fetchProject() {
      try {
        const base = await getProjectById(selectedProjectId);
        const nextClientName = String(base?.plant?.clientName || "DEMO").trim();

        setClientName(nextClientName);
      } catch (error) {
        console.error("Failed to fetch project clientName:", error);
        setClientName("DEMO");
      }
    }

    fetchProject();
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    if (!clientName) return;

    initializeAllTables(clientName);
  }, [selectedProjectId, clientName, initializeAllTables]);

  const comboInitializedRef = useRef(false);

  useEffect(() => {
    if (!specTable?.length) return;
    if (!Object.keys(allTables).length) return;
    if (!partLibTable || !Object.keys(partLibTable).length) return;
    if (comboInitializedRef.current) return;

    const savedTabData = partLibTable || {};

    if (!Object.keys(savedTabData).length) {
      comboInitializedRef.current = true;
      return;
    }

    const allSpecDataWithTab = specTable.map((row) => ({
      ...row,
      __tabName: getComponentTab(row?.Components, row?.Spec, allTables),
    }));

    const selectionMap = new Map();

    for (const tabName of ALL_PART_LIB_TABS) {
      const savedTab = savedTabData[tabName] || [];

      for (const savedRow of savedTab) {
        const { Spec, Components, OriginalSizeRange, Size } = savedRow;

        if (!Spec || !Components || !Size) continue;

        const range = OriginalSizeRange || Size;
        const mapKey = `${tabName}|${Spec}|${Components}|${range}`;

        if (!selectionMap.has(mapKey)) {
          selectionMap.set(mapKey, new Set());
        }

        selectionMap.get(mapKey).add(Size);
      }
    }

    const allRows = [];

    for (const tabName of ALL_PART_LIB_TABS) {
      const savedTab = savedTabData[tabName] || [];

      const componentGroups = new Map();

      for (const savedRow of savedTab) {
        const { Spec, Components, OriginalSizeRange, Size } = savedRow;

        if (!Spec || !Components) continue;

        const range = OriginalSizeRange || Size;
        const groupKey = `${Spec}|${Components}|${range}`;

        if (!componentGroups.has(groupKey)) {
          componentGroups.set(groupKey, savedRow);
        }
      }

      for (const sampleRow of componentGroups.values()) {
        const { Spec, Components, OriginalSizeRange } = sampleRow;

        const range = OriginalSizeRange || sampleRow.Size;
        const mapKey = `${tabName}|${Spec}|${Components}|${range}`;
        const selectedSizes = selectionMap.get(mapKey) || new Set();

        const sizes =
          getSizesOptions(allSpecDataWithTab, Spec, Components, allTables) ||
          [];

        const orgSizeRange = OriginalSizeRange || sampleRow.Size;

        const uniqueSizeValues = new Set();

        for (const s of sizes.filter((s) => s?.range === orgSizeRange)) {
          if (!s?.value || uniqueSizeValues.has(s.value)) continue;

          uniqueSizeValues.add(s.value);

          const newRow = {
            ...sampleRow,
            select: selectedSizes.has(s.value),
            TabName: tabName,
            Size: s.value,
            OriginalSizeRange: orgSizeRange,
            CategoryType: "Bulk",
          };

          try {
            updateDerivedFields(
              allSpecDataWithTab,
              (_i, key, value) => {
                newRow[key] = value;
              },
              allTables,
              0,
              newRow,
            );
          } catch {}

          delete newRow.__tabName;

          allRows.push(newRow);
        }
      }
    }

    if (allRows.length > 0) {
      comboInitializedRef.current = true;
      setAllCombinationTable(allRows);
    }
  }, [specTable, allTables, partLibTable]);

  return (
    <StructureModelingContext.Provider
      value={{
        selectedProjectId,
        setSelectedProjectId,

        partLibTable,
        setPartLibTable,

        specTable,
        setSpecTable,

        fullSpecTable,
        setFullSpecTable,

        estimationTable,
        setEstimationTable,

        allTables,
        initializeAllTables,

        selectedSpecs,
        setSelectedSpecs,
        addSlectedSpec,
        removeSelectedSpec,
        getSelectedSpecTable,

        getComponentCodeTable,

        modelingData,
        setModelingData,

        saveStructuralData,

        projectPath,
        setProjectPath,

        totalSummary,
        setTotalSummary,

        selectedModelingRevision,
        setSelectedModelingRevision,

        fastenersTable,
        setFastenersTable,

        bomData,
        setBomData,

        summaryData,
        setSummaryData,

        estimationTableWithTotalSummary,
        setEstimationTableWithTotalSummary,

        selectedEstimationRevision,
        setSelectedEstimationRevision,

        allCombinationTable,
        setAllCombinationTable,

        loadStructuralData,

        SelectedParts,

        clientName,
        setClientName,

        addNewEstimationRevision,
      }}
    >
      {children}
    </StructureModelingContext.Provider>
  );
};

export const useStructureModeling = () => {
  const context = useContext(StructureModelingContext);

  if (!context) {
    throw new Error(
      "useStructureModeling must be used within a StructureModelingProvider",
    );
  }

  return context;
};